import React, { useState, useRef, useEffect } from "react";
import Popover from "@material-ui/core/Popover";
import List from "@material-ui/core/List";
import { makeStyles } from "@material-ui/core/styles";
import {
  Grid,
  Slider,
  Typography,
  Button,
  Tooltip,
  IconButton
} from "@material-ui/core";
import { Volume2, VolumeX } from "lucide-react";
import IconButtonWithTooltip from "../ui/IconButtonWithTooltip";
import useSound from "use-sound";
import defaultAlertSound from "../../assets/sound.mp3";
import { getSoundUrl, playCompanyAlertSound } from "../../utils/soundHelper";
import { parseHeaderConfig } from "../../utils/headerConfig";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";

const useStyles = makeStyles((theme) => ({
  tabContainer: {
    padding: theme.spacing(1.5, 1.5, 0, 1.5),
    width: 300,
    [theme.breakpoints.down("sm")]: {
      width: 250,
    },
  },
  popoverPaper: {
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.palette.shadow.medium,
    overflow: "hidden",
  },
  popoverHeader: {
    backgroundColor: theme.palette.background.contrast,
    padding: theme.spacing(0, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  heroIcon: {
    width: 20,
    height: 20,
    color: theme.palette.text.primary,
  },
  volumeValue: {
    minWidth: 30,
    textAlign: "center",
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(1),
    fontWeight: 500,
  },
  testButton: {
    marginTop: theme.spacing(1),
    display: "flex",
    justifyContent: "center",
  },
  sliderContainer: {
    padding: theme.spacing(0, 0, 1),
  },
  sliderRoot: {
    color: theme.palette.primary.main,
    height: 4,
    padding: "13px 0",
  },
  active: {},
  selected: {},
  sliderThumb: {
    height: 14,
    width: 14,
    backgroundColor: theme.palette.primary.main,
    border: `2px solid ${theme.palette.background.paper}`,
    boxShadow: theme.palette.shadow.light,
    marginTop: -5,
    marginLeft: -7,
    '&:focus, &:hover, &$active': {
      boxShadow: `0px 0px 0px 8px ${theme.palette.action.hover}`,
    },
  },
  sliderActive: {},
  sliderTrack: {
    height: 4,
    borderRadius: 2,
  },
  sliderRail: {
    height: 4,
    borderRadius: 2,
    backgroundColor: theme.palette.action.hover,
  },
  presetButton: {
    minWidth: 34,
    height: 22,
    padding: "2px 6px",
    borderRadius: 12,
    fontSize: "0.7rem",
    backgroundColor: theme.palette.background.contrast,
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.active': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      border: `1px solid ${theme.palette.primary.main}`,
    },
    marginRight: theme.spacing(0.5),
  },
  presetContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  dialogTitle: {
    backgroundColor: theme.palette.background.contrast,
    '& h2': {
      fontSize: '1rem',
    }
  },
  dialogContent: {
    padding: theme.spacing(2),
  },
  dialogButton: {
    color: theme.palette.primary.main,
  },
  feedback: {
    position: 'fixed',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    padding: '8px 16px',
    borderRadius: '4px',
    boxShadow: theme.palette.shadow.medium,
    zIndex: 9999,
  },
  testIcon: {
    fontSize: '1rem',
    padding: 4,
  }
}));

const NotificationsVolume = ({ volume, setVolume, icon: Icon = null }) => {
  const classes = useStyles();

  const anchorRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [displayVolume, setDisplayVolume] = useState(typeof volume === "number" ? volume : Number(volume));
  const [alertSoundUrl, setAlertSoundUrl] = useState(() => {
    try {
      const stored = localStorage.getItem("headerConfig");
      const cfg = parseHeaderConfig(stored);
      return getSoundUrl(cfg.alertSound);
    } catch (_) {
      return defaultAlertSound;
    }
  });

  const normalizedVolume = typeof displayVolume === "number" ?
    Math.max(0, Math.min(1, displayVolume)) : 0;

  const [play] = useSound(alertSoundUrl, {
    volume: normalizedVolume,
    interrupt: true
  });

  useEffect(() => {
    const handleHeaderConfigUpdate = (event) => {
      const headerConfig = parseHeaderConfig(event.detail);
      setAlertSoundUrl(getSoundUrl(headerConfig.alertSound));
    };

    window.addEventListener("header-config-updated", handleHeaderConfigUpdate);
    return () => window.removeEventListener("header-config-updated", handleHeaderConfigUpdate);
  }, []);

  useEffect(() => {
    setDisplayVolume(typeof volume === "number" ? volume : Number(volume) || 1);
  }, [volume]);

  const handleClick = () => {
    setIsOpen((prevState) => !prevState);
  };

  const handleClickAway = () => {
    setIsOpen(false);
  };

  const handleVolumeChange = (event, newValue) => {
    setDisplayVolume(newValue);

    setVolume(newValue);
    localStorage.setItem("volume", newValue);
  };

  const handleVolumeChangeCommitted = () => {
  };

  const handlePresetClick = (level) => {
    const newVolume = level / 100;
    setDisplayVolume(newVolume);
    setVolume(newVolume);
    localStorage.setItem("volume", newVolume);

  };

  const handleTestVolume = () => {
    try {
      playCompanyAlertSound(normalizedVolume);
    } catch (_) {
      play();
    }
  };

  const formatVolumeDisplay = (value) => {
    return `${Math.round(value * 100)}%`;
  };

  const volumePresets = [0, 25, 50, 75, 100];

  const getPresetButtonClass = (preset) => {
    const volumePercent = Math.round(displayVolume * 100);
    return preset === volumePercent ? 'active' : '';
  };

  return (
    <>
      <div ref={anchorRef}>
        <IconButtonWithTooltip
          icon={Icon || (displayVolume > 0 ? Volume2 : VolumeX)}
          tooltip="Ajustar volume"
          onClick={handleClick}
          aria-label="Ajustar volume das notificações"
        />
      </div>

      <Popover
        disableScrollLock
        open={isOpen}
        anchorEl={anchorRef.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        classes={{ paper: classes.popoverPaper }}
        onClose={handleClickAway}
      >
        <div className={classes.popoverHeader}>
          <Typography variant="subtitle2">
            Volume das notificações
          </Typography>
          <Tooltip title="Testar volume">
            <IconButton
              size="small"
              onClick={handleTestVolume}
              className={classes.testIcon}
              color="primary"
            >
              <PlayArrowIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>

        <List dense className={classes.tabContainer}>
          <div className={classes.presetContainer}>
            {volumePresets.map(preset => (
              <Button
                key={preset}
                className={`${classes.presetButton} ${getPresetButtonClass(preset)}`}
                onClick={() => handlePresetClick(preset)}
                size="small"
              >
                {preset}%
              </Button>
            ))}
          </div>

          <div className={classes.sliderContainer}>
            <Grid container spacing={1} alignItems="center">
              <Grid item>
                <Tooltip title="Mudo">
                  <VolumeX
                    className={classes.heroIcon}
                    style={{
                      opacity: displayVolume === 0 ? 1 : 0.5,
                      cursor: 'pointer'
                    }}
                    onClick={() => handlePresetClick(0)}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs>
                <Slider
                  value={displayVolume}
                  aria-labelledby="volume-slider"
                  step={0.01}
                  min={0}
                  max={1}
                  onChange={handleVolumeChange}
                  onChangeCommitted={handleVolumeChangeCommitted}
                  classes={{
                    root: classes.sliderRoot,
                    thumb: classes.sliderThumb,
                    active: classes.sliderActive,
                    track: classes.sliderTrack,
                    rail: classes.sliderRail,
                  }}
                />
              </Grid>
              <Grid item>
                <Tooltip title="Volume máximo">
                  <Volume2
                    className={classes.heroIcon}
                    style={{
                      opacity: displayVolume > 0.75 ? 1 : 0.5,
                      cursor: 'pointer'
                    }}
                    onClick={() => handlePresetClick(100)}
                  />
                </Tooltip>
              </Grid>
              <Grid item className={classes.volumeValue}>
                {formatVolumeDisplay(displayVolume)}
              </Grid>
            </Grid>
          </div>
        </List>
      </Popover>
    </>
  );
};

export default NotificationsVolume;
